import { css } from 'aphrodite/no-important';
import React, { FC, useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { DropdownDownIcon } from 'util/icons';
import { palette } from 'util/style-helpers';
import { style } from './style';

export interface ILink {
  text: string;
  sub_text?: string;
  route: string;
  icon?: string;
}

export interface ISubLink {
  text: string;
  route: string;
  icon?: string;
}
interface Props {
  link: ILink;
  sublinks?: ISubLink[];
  isVisibleOnMobile?: boolean;
}
interface LocationState {
  from: {
    pathname: string;
  };
}

export const NavLinkItem: FC<Props> = ({ link, sublinks, isVisibleOnMobile }) => {
  const location = useLocation<LocationState>();

  const hasDropdown = !!sublinks && sublinks.length;
  const numberOfSublinks = sublinks?.length || 0;
  const isSelected = location.pathname === link.route;

  const [touches, setTouches] = useState(0);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const styles = style({ isSelected, hasDropdown, isDropdownOpen, numberOfSublinks, isVisibleOnMobile });

  const reset = () => {
    setTouches(0);
    setIsDropdownOpen(false);
  };

  useEffect(() => {
    function handleDocumentTouchStartEvent(event: any) {
      if (containerRef.current && !containerRef.current.contains(event.target as Element)) {
        reset();
      }
    }

    if (containerRef.current) document.addEventListener('touchstart', handleDocumentTouchStartEvent);
    return () => document.removeEventListener('touchstart', handleDocumentTouchStartEvent);
  }, [containerRef]);

  const onLinkClick = (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
    const isTouchDevice = touches > 0;
    const isSecondTouch = touches === 2; // Double click step for tablets
    if (isDropdownOpen) {
      // Prevent redirection on first touch.
      if (isTouchDevice && !isSecondTouch) event.preventDefault();
      else if (isTouchDevice) reset();
    }
  };

  const openDropdown = () => {
    if (hasDropdown && !isDropdownOpen) {
      setIsDropdownOpen(true);
    }
  };

  const onSubLinkClick = (sublink: ISubLink, event: any) => {
    const isTouchDevice = touches > 0;
    if (isTouchDevice) reset();
  };

  const getLink = (): JSX.Element => {
    return (
      <div
        ref={(node) => (containerRef.current = node)}
        className={css(styles.linkContainer)}
        onTouchEnd={() => {
          if (!isDropdownOpen) openDropdown();
          setTouches(touches + 1);
        }}
        onMouseEnter={(event) => {
          // Hover:: Tablet & Desktop
          openDropdown();
        }}
        onMouseLeave={() => {
          reset();
        }}>
        <div className={css(styles.linkHeader)}>
          <Link to={link.route} onClick={(event) => onLinkClick(event)}>
            {link.text}
          </Link>
          {hasDropdown && (
            <div className={css(styles.dropdownArrowIndicator)}>
              {isDropdownOpen ? <DropdownDownIcon color={palette.red} /> : <DropdownDownIcon color={'#C1C0C0'} />}
            </div>
          )}
        </div>
        {hasDropdown && (
          <div className={`dropdown-reference ${css(styles.dropdownContainer)}`}>
            {sublinks!.map((sublink: ISubLink, index: number) => {
              return (
                <Link
                  key={index}
                  className={css(styles.dropdownLink)}
                  to={sublink.route}
                  onClick={(event) => onSubLinkClick(sublink, event)}>
                  <span className={css(styles.dropdownLinkContent)}>
                    {sublink.icon && <img src={sublink.icon} alt={sublink.text} />}
                    {sublink.text}
                  </span>
                </Link>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  return getLink();
};
